// ============================================================
// period — janelas de dia / semana / mês, e agrupamento por balde
// ============================================================
// O dono pediu "registros das contas organizados por dia, semana, mês".
//
// DUAS PERGUNTAS DIFERENTES, e misturá-las é o erro fácil:
//
//   1. "O QUE ACONTECEU NESTE PERÍODO?" — uma janela só, a corrente.
//      É o que a FILA DE BAN usa: hoje / esta semana / este mês.
//      → `janelaDoPeriodo`
//
//   2. "COMO ISSO SE DISTRIBUI AO LONGO DO TEMPO?" — várias janelas
//      consecutivas, uma por balde. É o que o HISTÓRICO DA CONTA usa:
//      30 dias em baldes de dia, 12 semanas em baldes de semana.
//      → `janelaDeHistorico` + `agruparPorPeriodo`
//
// ⚠️ TUDO EM HORA LOCAL, de propósito. O operador está no Chile e decide
// "isso foi ontem" pelo relógio dele, não por UTC. Bucketizar em UTC
// empurraria as detecções da madrugada para o dia seguinte na tela — e a
// pergunta que o dono faz é "quem fez o quê ontem", não "quem fez o quê no
// intervalo UTC que por acaso se chama ontem".
// ============================================================

export type PeriodKey = 'day' | 'week' | 'month';

export const PERIOD_KEYS: readonly PeriodKey[] = ['day', 'week', 'month'];

/** Rótulo curto do seletor. */
export function rotuloDoPeriodo(key: PeriodKey): string {
  switch (key) {
    case 'day':   return 'Dia';
    case 'week':  return 'Semana';
    case 'month': return 'Mês';
  }
}

/** Rótulo da janela CORRENTE (o que a fila de ban está olhando agora). */
export function rotuloDaJanela(key: PeriodKey): string {
  switch (key) {
    case 'day':   return 'hoje';
    case 'week':  return 'esta semana';
    case 'month': return 'este mês';
  }
}

export interface Janela {
  /** ISO-8601 — início inclusivo. */
  from: string;
  /** ISO-8601 — fim exclusivo. */
  to: string;
}

/**
 * Início do balde que contém `d`, em hora local.
 *
 * Semana começa na SEGUNDA — convenção de Brasil/Chile. `getDay()` devolve
 * 0 para domingo, então domingo pertence à semana que começou 6 dias antes,
 * e não à que começa amanhã.
 */
export function inicioDoBalde(d: Date, key: PeriodKey): Date {
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  switch (key) {
    case 'day':
      return base;
    case 'week': {
      const diaDaSemana = base.getDay();            // 0=dom … 6=sáb
      const recuo = diaDaSemana === 0 ? 6 : diaDaSemana - 1;
      base.setDate(base.getDate() - recuo);
      return base;
    }
    case 'month':
      return new Date(d.getFullYear(), d.getMonth(), 1);
  }
}

/** Início do balde seguinte — fim exclusivo do balde de `d`. */
export function proximoBalde(d: Date, key: PeriodKey): Date {
  const ini = inicioDoBalde(d, key);
  switch (key) {
    case 'day':
      return new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + 1);
    case 'week':
      return new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + 7);
    case 'month':
      return new Date(ini.getFullYear(), ini.getMonth() + 1, 1);
  }
}

/**
 * Janela CORRENTE: do início do período até agora.
 *
 * `to` é o instante atual e não o fim do período — pedir eventos do futuro
 * não devolve nada e ainda faz o backend varrer índice à toa.
 */
export function janelaDoPeriodo(key: PeriodKey, agora: Date = new Date()): Janela {
  return {
    from: inicioDoBalde(agora, key).toISOString(),
    to: agora.toISOString(),
  };
}

/**
 * Janela do período em HORAS — o que `/admin/ban-queue?window_hours=` aceita
 * (1..720, ver BanQueueController.h).
 *
 * "Mês" são 720 h corridas e NÃO o mês do calendário: 720 é o teto do backend,
 * e um janeiro de 744 h seria rejeitado. Uma tela que falha conforme o mês é
 * pior que uma que sempre mostra os últimos 30 dias — e a fila de ban é sobre
 * "quem está cheatando agora", não sobre fechamento contábil.
 */
export function horasDoPeriodo(key: PeriodKey): number {
  switch (key) {
    case 'day':   return 24;
    case 'week':  return 168;
    case 'month': return 720;
  }
}

/** Quantos baldes o histórico mostra por padrão, por granularidade. */
export const BALDES_PADRAO: Record<PeriodKey, number> = {
  day: 30,     // um mês em dias
  week: 12,    // um trimestre em semanas
  month: 12,   // um ano em meses
};

/**
 * Janela do HISTÓRICO: `quantidade` baldes para trás, terminando agora.
 *
 * Anda balde a balde em vez de subtrair milissegundos porque mês não tem
 * duração fixa e horário de verão faz dia ter 23 ou 25 horas — subtrair
 * `30 * 86400000` erra a borda exatamente nos dias que interessam.
 */
export function janelaDeHistorico(
  key: PeriodKey,
  quantidade: number = BALDES_PADRAO[key],
  agora: Date = new Date(),
): Janela {
  let cursor = inicioDoBalde(agora, key);
  for (let i = 1; i < Math.max(1, quantidade); i++) {
    cursor = baldeAnterior(cursor, key);
  }
  return { from: cursor.toISOString(), to: agora.toISOString() };
}

function baldeAnterior(inicio: Date, key: PeriodKey): Date {
  switch (key) {
    case 'day':
      return new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() - 1);
    case 'week':
      return new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() - 7);
    case 'month':
      return new Date(inicio.getFullYear(), inicio.getMonth() - 1, 1);
  }
}

/** Rótulo humano do balde — "07/08/2026", "semana de 03/08", "ago/2026". */
export function rotuloDoBalde(inicio: Date, key: PeriodKey): string {
  const dd = String(inicio.getDate()).padStart(2, '0');
  const mm = String(inicio.getMonth() + 1).padStart(2, '0');
  switch (key) {
    case 'day':
      return `${dd}/${mm}/${inicio.getFullYear()}`;
    case 'week': {
      const fim = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + 6);
      const fdd = String(fim.getDate()).padStart(2, '0');
      const fmm = String(fim.getMonth() + 1).padStart(2, '0');
      return `${dd}/${mm} a ${fdd}/${fmm}`;
    }
    case 'month': {
      const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun',
                     'jul', 'ago', 'set', 'out', 'nov', 'dez'];
      return `${meses[inicio.getMonth()]}/${inicio.getFullYear()}`;
    }
  }
}

export interface Balde<T> {
  /** ISO do início do balde — chave estável para `key` de lista React. */
  inicio: string;
  rotulo: string;
  itens: T[];
}

/**
 * Agrupa itens em baldes de dia/semana/mês, do mais RECENTE para o mais antigo.
 *
 * Baldes vazios NÃO são preenchidos: a tela é uma lista de "o que aconteceu",
 * e 27 linhas vazias entre duas detecções enterrariam as duas. Item com data
 * inválida é descartado em vez de virar balde "Invalid Date" — payload torto
 * não pode derrubar a página.
 */
export function agruparPorPeriodo<T>(
  itens: readonly T[],
  dataDe: (item: T) => string | Date | null | undefined,
  key: PeriodKey,
): Array<Balde<T>> {
  const mapa = new Map<number, Balde<T>>();

  for (const item of itens) {
    const bruto = dataDe(item);
    if (!bruto) continue;
    const d = bruto instanceof Date ? bruto : new Date(bruto);
    if (Number.isNaN(d.getTime())) continue;

    const ini = inicioDoBalde(d, key);
    const chave = ini.getTime();
    let balde = mapa.get(chave);
    if (!balde) {
      balde = { inicio: ini.toISOString(), rotulo: rotuloDoBalde(ini, key), itens: [] };
      mapa.set(chave, balde);
    }
    balde.itens.push(item);
  }

  return [...mapa.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([, balde]) => balde);
}
